import type { IntlShape } from "react-intl";

import CardType from "@app/enum/dashboard/CardType";

import type { CardItemProps } from "../components/CardItem";

const useCardInfo = (
  intl: IntlShape
): Record<CardType, Pick<CardItemProps, "description" | "icon" | "title">> => {
  return {
    [CardType.ACTIVITIES_LOGGED]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.activitiesLogged.description",
        defaultMessage: "Activities • D/W/M/Q/Y • Goal Compatible",
        description:
          "Description of the Activities Logged card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.activitiesLogged.title",
        defaultMessage: "Activities Tracker",
        description: "Title of the Activities Logged card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.ACTIVITIES_OVERDUE]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.activitiesOverdue.description",
        defaultMessage: "Activities • Current State • Goal Compatible",
        description:
          "Description of the Activities Overdue card in Add Card wizard on the Dashboard page",
      }),
      icon: "monitor",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.activitiesOverdue.title",
        defaultMessage: "Overdue Activity Monitor",
        description:
          "Title of the Activities Overdue card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.CHECK_INS]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.checkIns.description",
        defaultMessage: "Activities (Check-Ins only) • D/W/M/Q/Y • Goal Compatible",
        description: "Description of the Check-Ins card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.checkIns.title",
        defaultMessage: "Check-In Tracker",
        description: "Title of the Check-Ins card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.CLOSED_STAGE_DEAL_AMOUNT]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.closedStageDealAmount.description",
        defaultMessage: "Deals • D/W/M/Q/Y • Goal Compatible",
        description:
          "Description of the closedStageDealAmount card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.closedStageDealAmount.title",
        defaultMessage: "Closed Stage Deal Amount Tracker",
        description:
          "Title of the Closed Stage Deal Amount card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.CLOSED_STAGE_DEAL_COUNT]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.closedStageDealCount.description",
        defaultMessage: "Deals • D/W/M/Q/Y • Goal Compatible",
        description:
          "Description of the closedStageDealCount card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.closedStageDealCount.title",
        defaultMessage: "Closed Stage Deal Count Tracker",
        description:
          "Title of the Closed Stage Deal Count card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.CUSTOMER_FACE_TIME]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.customerFaceTime.description",
        defaultMessage: "Activities (Two-Step Check-Ins Only) • D/W/M/Q/Y • Goal Compatible",
        description:
          "Description of the customerFaceTime card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.customerFaceTime.title",
        defaultMessage: "Customer Face Time Tracker",
        description: "Title of the Customer Face card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.MILEAGE_TRACKING]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.mileageTracking.description",
        defaultMessage: "Mileage Tracker • D/W/M/Q/Y • Goal Compatible",
        description:
          "Description of the mileageTracking card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.mileageTracking.title",
        defaultMessage: "Drive Time Tracker",
        description: "Title of the Mileage Tracker card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.NEW_RECORDS]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.newRecords.description",
        defaultMessage: "Companies, People, Deals • D/W/M/Q/Y • Goal Compatible",
        description: "Description of the New Records card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.newRecords.title",
        defaultMessage: "New Records Tracker",
        description: "Title of the New Records card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.NEW_ROUTES]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.newRoutes.description",
        defaultMessage: "Companies, People • D/W/M/Q/Y • Goal Compatible",
        description: "Description of the New Routes card in Add Card wizard on the Dashboard page",
      }),
      icon: "chart",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.newRoutes.title",
        defaultMessage: "New Routes Tracker",
        description: "Title of the New Routes card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.NO_CONTACT_IN]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.noContactIn.description",
        defaultMessage: "Companies, People, Deals • Current State",
        description:
          "Description of the No Contact In card in Add Card wizard on the Dashboard page",
      }),
      icon: "map",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.noContactIn.title",
        defaultMessage: "No Recent Contact Mini-Map",
        description: "Title of the No Contact In card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.NO_CONTACT_IN_COUNT]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.noContactInCount.description",
        defaultMessage: "Companies, People, Deals • Current State • Goal Compatible",
        description:
          "Description of the No Contact In Count card in Add Card wizard on the Dashboard page",
      }),
      icon: "monitor",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.noContactInCount.title",
        defaultMessage: "No Recent Contact Monitor",
        description:
          "Title of the No Recent Contact Count card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.OPEN_STAGE_DEAL_AMOUNT]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.openStageDealAmount.description",
        defaultMessage: "Deals • Current State • Goal Compatible",
        description:
          "Description of the Open Stage Deal Amount Monitor in Add Card wizard on the Dashboard page",
      }),
      icon: "monitor",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.openStageDealAmount.title",
        defaultMessage: "Open Stage Deal Amount Monitor",
        description:
          "Title of the Open Stage Deal Amount Monitor in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.OUT_OF_FREQUENCY]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.outOfFrequency.description",
        defaultMessage: "Companies, People, Deals • Current State • Goal Compatible",
        description:
          "Description of the Out of Frequency card in Add Card wizard on the Dashboard page",
      }),
      icon: "map",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.outOfFrequency.title",
        defaultMessage: "Record Frequency Mini-Map",
        description: "Title of the Out of Frequency card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.RECORDS_PAST_DUE]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.recordsPastDue.description",
        defaultMessage: "Companies, People, Deals • Current State • Goal Compatible",
        description:
          "Description of the Records Past Due card in Add Card wizard on the Dashboard page",
      }),
      icon: "monitor",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.recordsPastDue.title",
        defaultMessage: "Record Frequency Monitor",
        description: "Title of the Records Past Due card in Add Card wizard on the Dashboard page",
      }),
    },
    [CardType.UPCOMING_ACTIVITIES]: {
      description: intl.formatMessage({
        id: "dashboard.addEditCard.types.upcomingActivities.description",
        defaultMessage: "Companies, People, Deals • Current State",
        description:
          "Description of the Upcoming Activities card in Add Card wizard on the Dashboard page",
      }),
      icon: "map",
      title: intl.formatMessage({
        id: "dashboard.addEditCard.types.upcomingActivities.title",
        defaultMessage: "Upcoming Activities Mini-Map",
        description:
          "Title of the RUpcoming Activities card in Add Card wizard on the Dashboard page",
      }),
    },
  };
};

export default useCardInfo;
